import { z } from 'zod';
import { MetaSchema } from './create-chat.js';
import { MemberRoleSchema } from './me.js';

/**
 * `GET /api/v1/usage` contracts (ADR-0063, D13-05) — this user's current-period
 * consumption and the org aggregate, off the D13-04 ledger. `data` is strict (the
 * BFF authors it); the outer envelope is loose (ADR-0024 §11, tolerant-out).
 *
 * `costAud`/`credits` are **absent, not `"0"`**, wherever the underlying ledger row
 * has no priced total — the D13-03 null discipline carried all the way to the wire,
 * so the SPA can render "not yet priced" rather than a misleading free-looking `$0`.
 */

const UsageTokensSchema = z.strictObject({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cacheReadTokens: z.number().int().nonnegative(),
  cacheWriteTokens: z.number().int().nonnegative(),
});

/** One party's (the caller's, or the whole org's) consumption for the period. */
const UsageShareSchema = z.strictObject({
  turns: z.number().int().nonnegative(),
  modelCalls: z.number().int().nonnegative(),
  tokens: UsageTokensSchema,
  /** AUD, decimal string. Absent — never `"0"` — if unpriced. */
  costAud: z.string().optional(),
  /** Display credits, projected via the D13-03 `creditRateAud`. Absent if unpriced. */
  credits: z.string().optional(),
});

const OrgUsageShareSchema = UsageShareSchema.extend({
  /** How many org members contributed a row this period (0 = nobody has run a turn yet). */
  memberCount: z.number().int().nonnegative(),
});

/** `data` of `GET /api/v1/usage`. */
export const UsageDataSchema = z.strictObject({
  /** Current UTC billing period (`YYYY-MM`), server-determined — never client-supplied. */
  period: z.string(),
  you: UsageShareSchema,
  org: OrgUsageShareSchema,
  /**
   * Echoes `period` when every contributing row this period has a priced total;
   * absent when any does, signalling the `costAud`/`credits` figures above may be
   * an undercount rather than complete. Not a per-share flag — one signal for the
   * whole response, since "you" is always a subset of the org rows checked.
   */
  pricedThrough: z.string().optional(),
});
export type UsageData = z.infer<typeof UsageDataSchema>;

export const UsageResponseSchema = z.looseObject({
  data: UsageDataSchema,
  meta: MetaSchema,
});
export type UsageResponse = z.infer<typeof UsageResponseSchema>;

/**
 * `GET /api/v1/workstreams/usage/ledger` contracts (ADR-0076 §3, D16-03) — one
 * row per billable turn for a period. Owner/Admin get every member's turns;
 * a Member gets only their own. The whole period, newest first, no cursor (the
 * SPA filters by agent/user/model/workstream client-side).
 */

/** `YYYY-MM`. The BFF also bounds it to the last 12 periods. */
export const UsagePeriodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'must be YYYY-MM');

/** Query string of the ledger read. Absent `period` = the current one. */
export const UsageLedgerQuerySchema = z.strictObject({
  period: UsagePeriodSchema.optional(),
});
export type UsageLedgerQuery = z.infer<typeof UsageLedgerQuerySchema>;

const UsageLedgerRowSchema = z.strictObject({
  turnId: z.string(),
  /** RFC-3339 UTC — when the turn was recorded. */
  at: z.string(),
  workstreamId: z.string(),
  /** Workstream title as it was when the turn ran. */
  title: z.string(),
  /** Auth0 `sub` of the member who ran the turn. */
  sub: z.string(),
  /** Agents that produced the turn, in execution order. */
  agents: z.array(z.string()),
  /**
   * ADR-0080 AB-1-4: the version each custom/curated member agent ran at, by
   * agent id (platform agents have no entry). Absent on rows without one.
   */
  agentVersions: z.record(z.string(), z.number().int().positive()).optional(),
  /** AUD per model, decimal string; `null` = that model was not priced. */
  byModel: z.record(z.string(), z.string().nullable()),
  modelCalls: z.number().int().nonnegative(),
  tokens: UsageTokensSchema,
  /** AUD, decimal string. Absent — never `"0"` — if the turn was unpriced. */
  costAud: z.string().optional(),
  /**
   * Credits (= dollars shown, ADR-0078 §1): `costAud ÷ creditRate`, decimal string.
   * Absent with `costAud`, or when no credit rate is configured.
   */
  credits: z.string().optional(),
  /**
   * ADR-0078 §5: `overage` when the period had already used its allowance (plus
   * extensions) before this turn ran. Absent when the tenant has no plan, the total
   * was unknown, or the row predates D17-08.
   */
  billedAs: z.enum(['included', 'overage']).optional(),
});
export type UsageLedgerRow = z.infer<typeof UsageLedgerRowSchema>;

export const UsageLedgerDataSchema = z.strictObject({
  period: UsagePeriodSchema,
  rows: z.array(UsageLedgerRowSchema),
});
export type UsageLedgerData = z.infer<typeof UsageLedgerDataSchema>;

export const UsageLedgerResponseSchema = z.looseObject({
  data: UsageLedgerDataSchema,
  meta: MetaSchema,
});
export type UsageLedgerResponse = z.infer<typeof UsageLedgerResponseSchema>;

/**
 * `GET /api/v1/workstreams/usage/report` contracts (ADR-0071 §3, D15-04) — one
 * read surface for Agent Monitor's Overview and Reports, **shaped by the
 * caller's role**. A Member gets `you` + `org` and nothing else; Owner/Admin
 * also get the breakdowns. The Member response carries NO breakdown key at all
 * (absent, never empty), so the SPA can hide by key and a leak is testable on
 * the serialised body.
 *
 * `costAud`/`credits` are absent — never `"0"` — wherever the underlying rows
 * have no priced total (the D13-03 null discipline).
 */

/** Query string of the report read. Absent `period` = the current one. */
export const UsageReportQuerySchema = z.strictObject({
  period: UsagePeriodSchema.optional(),
});
export type UsageReportQuery = z.infer<typeof UsageReportQuerySchema>;

const ReportFiguresSchema = z.strictObject({
  /** AUD, decimal string. Absent — never `"0"` — if unpriced. */
  costAud: z.string().optional(),
  /** Display credits via the D13-03 `creditRateAud`. Absent if unpriced. */
  credits: z.string().optional(),
});

const ReportYouSchema = ReportFiguresSchema.extend({ turns: z.number().int().nonnegative() });

const ReportOrgSchema = ReportYouSchema.extend({ memberCount: z.number().int().nonnegative() });

export const UsageReportDataSchema = z.strictObject({
  role: MemberRoleSchema,
  period: UsagePeriodSchema,
  you: ReportYouSchema,
  org: ReportOrgSchema,
  // ── Owner/Admin only — ABSENT for a Member ──────────────────────────────
  /** UTC days with spend, ascending; days with no turns are omitted. */
  daily: z.array(ReportFiguresSchema.extend({ day: z.string().regex(/^\d{2}$/) })).optional(),
  byWorkstream: z
    .array(
      ReportFiguresSchema.extend({
        workstreamId: z.string(),
        /** Title as of the last turn in the period. */
        title: z.string(),
      }),
    )
    .optional(),
  /** Auth0 `sub`, as on the ledger (D16-03), so the SPA can join the two. */
  byMember: z
    .array(
      ReportFiguresSchema.extend({
        sub: z.string(),
        /** Display name stamped on the ledger row (D15-16); absent on rows written before it. */
        name: z.string().optional(),
        turns: z.number().int().nonnegative(),
      }),
    )
    .optional(),
  /** The six periods ending at `period`, oldest first. */
  history: z.array(ReportFiguresSchema.extend({ period: UsagePeriodSchema })).optional(),
  /** Allowance only — the AUD billing figures are Owner-only and live on D15-09's surface. */
  plan: z
    .strictObject({
      name: z.string(),
      includedCredits: z.string(),
      illustrative: z.boolean(),
    })
    .optional(),
  /**
   * `true` only when the daily and by-workstream roll-ups account for every
   * turn in the org total. `false` for a period that began before roll-ups
   * shipped (or lost a roll-up write to contention) — the SPA then shows the
   * totals but not charts that would not reconcile.
   */
  rollupsAvailable: z.boolean().optional(),
});
export type UsageReportData = z.infer<typeof UsageReportDataSchema>;

export const UsageReportResponseSchema = z.looseObject({
  data: UsageReportDataSchema,
  meta: MetaSchema,
});
export type UsageReportResponse = z.infer<typeof UsageReportResponseSchema>;
